import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { defaultConfig } from "../config/defaultConfig";
import StatsPanel from "./StatsPanel";
import {
  addWeightToHanger,
  buildHangers,
  buildScaleValues,
  computeBalance,
  ensureOdd,
  getCenterValue,
  moveOneWeight,
  deleteOneWeight
} from "../core/balanceEngine";

export default function Balance() {
  const { t, i18n } = useTranslation();

  const toggleLang = () => {
    const next = i18n.language === "es" ? "en" : "es";
    i18n.changeLanguage(next);
    localStorage.setItem("balanza-lang", next);
  };

  const [cfg, setCfg] = useState(() => ({
    ...defaultConfig
  }));
  const SNAP_DISTANCE = 80;
  const [showConfig, setShowConfig] = useState(false);
  const [hoverHanger, setHoverHanger] = useState(null);

  const scaleValues = useMemo(() => {
    return buildScaleValues({
      hangersCount: cfg.hangersCount,
      startValue: cfg.startValue,
      step: cfg.step
    });
  }, [cfg.hangersCount, cfg.startValue, cfg.step]);

  const [hangers, setHangers] = useState(() => buildHangers(scaleValues));

  const centerValue = useMemo(() => getCenterValue(scaleValues), [scaleValues]);
  const [mastValue, setMastValue] = useState(centerValue);

  const barRef = useRef(null);
  const hangerRefs = useRef({});

  const [drag, setDrag] = useState(null);
  // drag = { pointerId, fromHangerId, fromSource, x, y }

  const [mastDrag, setMastDrag] = useState(null);
  // mastDrag = { pointerId }

  const [movableMast, setMovableMast] = useState(false);

  // Input del mástil: estado local como string para admitir "-", "3.", "3,2", etc.
  const [mastRawValue, setMastRawValue] = useState(String(centerValue));
  const mastInputFocused = useRef(false);

  useEffect(() => {
    setHangers(buildHangers(scaleValues));
    setMastValue(getCenterValue(scaleValues));
  }, [scaleValues]);

  useEffect(() => {
    if (cfg.mastMode === "fixed") {
      setMastValue(centerValue);
    }
  }, [cfg.mastMode, centerValue]);

  const effectiveMastValue = cfg.mastMode === "fixed"
    ? centerValue
    : mastValue;

  const balance = useMemo(() => {
    return computeBalance({ hangers, mastValue: effectiveMastValue });
  }, [hangers, effectiveMastValue]);

  const showMastNow = cfg.showMast;
  const showSumaDistancias = cfg.showSumaDistancias;
  // Las estadísticas solo están disponibles cuando el mástil es visible
  const statsEnabled = cfg.showMast;

  const rotationDeg = useMemo(() => {
    if (!cfg.showMast) return 0;

    const levels = [0, 3, 7, 12, 18];
    const sign = balance.state === "left"
      ? -1
      : balance.state === "right"
        ? 1
        : 0;

    const raw = levels[balance.tiltLevel] * sign;

    const maxTilt = 18;
    if (raw > maxTilt) return maxTilt;
    if (raw < -maxTilt) return -maxTilt;

    return raw;
  }, [balance.state, balance.tiltLevel, cfg.showMast]);

  const updateCfg = patch => setCfg(prev => ({
    ...prev,
    ...patch
  }));

  const onChangeNumber = (key, value) => {
    const num = Number(value);
    if (Number.isNaN(num)) return;
    updateCfg({ [key]: num });
  };

  const onChangeHangersCount = value => {
    let num = Number(value);
    if (Number.isNaN(num)) return;
    if (num > 37) num = 37;
    if (num < 1) num = 1;
    num = ensureOdd(num);
    if (num > 37) num = 37;
    updateCfg({ hangersCount: num });
  };

  // Drag global (móvil)
  useEffect(() => {
    const onMove = e => {
      if (drag && e.pointerId === drag.pointerId) {
        setDrag(prev => ({
          ...prev,
          x: e.clientX,
          y: e.clientY
        }));

        let bestId = null;
        let bestDist = Infinity;

        hangers.forEach((h, index) => {
          const isMiddle = index === Math.floor(hangers.length / 2);
          if (isMiddle) return;

          const el = hangerRefs.current[h.id];
          if (!el) return;

          const r = el.getBoundingClientRect();
          const cx = r.left + r.width / 2;
          const dist = Math.abs(cx - e.clientX);

          if (dist < bestDist) {
            bestDist = dist;
            bestId = h.id;
          }
        });

        setHoverHanger(bestId);
      }

      if (mastDrag && e.pointerId === mastDrag.pointerId) {
        if (cfg.mastMode !== "movable" || !movableMast) return;

        const bar = barRef.current;
        if (!bar) return;

        const x = e.clientX;

        let bestIdx = 0;
        let bestDist = Infinity;

        hangers.forEach((h, i) => {
          const el = hangerRefs.current[h.id];
          if (!el) return;

          const r = el.getBoundingClientRect();
          const cx = r.left + r.width / 2;
          const d = Math.abs(cx - x);

          if (d < bestDist) {
            bestDist = d;
            bestIdx = i;
          }
        });

        setMastValue(scaleValues[bestIdx]);
      }
    };

    const dropWeight = (clientX, clientY) => {
      if (!barRef.current) return { dropped: false, action: "none" };

      let bestId = null;
      let bestDist = Infinity;

      hangers.forEach(h => {
        const el = hangerRefs.current[h.id];
        if (!el) return;
        const r = el.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const dist = Math.abs(cx - clientX);

        if (drag?.fromSource === "tray") {
          if (dist < bestDist && dist < SNAP_DISTANCE) { bestDist = dist; bestId = h.id; }
        } else {
          if (dist < bestDist && dist < SNAP_DISTANCE * 1.5) { bestDist = dist; bestId = h.id; }
        }
      });

      if (bestId == null) {
        return drag?.fromSource === "hanger"
          ? { dropped: true, action: "delete" }
          : { dropped: true, action: "none" };
      }
      return { dropped: true, action: "move", toHangerId: bestId };
    };

    const onUp = e => {
      if (drag && e.pointerId === drag.pointerId) {
        const { action, toHangerId } = dropWeight(e.clientX, e.clientY);

        if (drag.fromSource === "tray") {
          // 👉 viene del cajón → SIEMPRE añade
          if (action === "move" && Number.isFinite(toHangerId)) {
            setHangers(prev => addWeightToHanger(prev, toHangerId));
          }
        } else {
          // 👉 viene de una percha (comportamiento actual)
          if (action === "delete") {
            setHangers(prev => deleteOneWeight(prev, drag.fromHangerId));
          }

          if (action === "move" && Number.isFinite(toHangerId)) {
            setHangers(prev => moveOneWeight(prev, drag.fromHangerId, toHangerId));
          }
        }

        setDrag(null);
        setHoverHanger(null);
      }

      if (mastDrag && e.pointerId === mastDrag.pointerId) {
        setMastDrag(null);
      }
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerup", onUp, { passive: true });

    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [drag, mastDrag, cfg.mastMode, scaleValues, hangers, movableMast]);

  useEffect(() => {
    if (!drag && !mastDrag) return;

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = prev;
    };
  }, [drag, mastDrag]);

  const onWeightPointerDown = (e, fromHangerId) => {
    e.preventDefault();
    e.stopPropagation();

    e.currentTarget.setPointerCapture(e.pointerId);

    setDrag({
      pointerId: e.pointerId,
      fromHangerId,
      fromSource: "hanger",
      x: e.clientX,
      y: e.clientY
    });
  };

  const onTrayWeightPointerDown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    e.currentTarget.setPointerCapture(e.pointerId);

    setDrag({
      pointerId: e.pointerId,
      fromHangerId: null,
      fromSource: "tray",
      x: e.clientX,
      y: e.clientY
    });
  };

  const onMastPointerDown = e => {
    if (cfg.mastMode !== "movable" || !movableMast) return;

    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    setMastDrag({ pointerId: e.pointerId });
  };

  // Sincronizar mastRawValue cuando mastValue cambia externamente (arrastre, reset, config)
  useEffect(() => {
    if (!mastInputFocused.current) {
      setMastRawValue(String(mastValue));
    }
  }, [mastValue]);

  // Inicializar mastMode al montar
  useState(() => updateCfg({ mastMode: "movable" }));

  const mastIndex = useMemo(() => {
    if (!scaleValues.length) return 0;

    let closestIndex = 0;
    let minDiff = Infinity;

    scaleValues.forEach((v, i) => {
      const diff = Math.abs(v - effectiveMastValue);
      if (diff < minDiff) {
        minDiff = diff;
        closestIndex = i;
      }
    });

    return closestIndex;
  }, [scaleValues, effectiveMastValue]);

  const mastLeftPct = scaleValues.length > 1
    ? (mastIndex / (scaleValues.length - 1)) * 100
    : 50;


  const middleIndex = Math.floor(hangers.length / 2);

  const wrapRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const updateScale = () => {
      const containerWidth = window.innerWidth * 0.9;

      if (!wrapRef.current) return;

      const contentWidth = wrapRef.current.offsetWidth;

      const newScale = Math.min(1, containerWidth / contentWidth);

      setScale(newScale);
    };

    updateScale();
    window.addEventListener("resize", updateScale);

    return () => window.removeEventListener("resize", updateScale);
  }, []);

  return (
    <div className="balance-root">
      <header className="topbar">
        <div className="row align-items-center">
          <div className="col">
            <h3>{t("appTitle")} <span>{t("appVersion")}</span></h3>
          </div>
          <div className="col text-center">
            <div className="estadoBalanza">
              {showSumaDistancias && (
                <>
                  {/* <span className={`pill ${balance.state}`}>
                    {
                      balance.state === "balanced"
                        ? "Equilibrada"
                        : balance.state === "left"
                          ? "Izquierda"
                          : "Derecha"
                    }
                  </span> */}
                  {t("distanceSum.label")}
                  <span className="pill soft ms-2">
                    {t("distanceSum.left")}: {balance.left} | {t("distanceSum.right")}: {balance.right}
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="col d-flex justify-content-end align-items-center gap-3">
            <div>
              
            </div>
            <div className="mostrarMastil">
              <label htmlFor="showMast">{t("topbar.mast")} </label>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={cfg.showMast}
                  onChange={e => updateCfg({ showMast: e.target.checked })}
                />
                <span className="slider"></span>
              </label>
            </div>
            <div className="bloquearMastil">
              <label htmlFor="movableMast">{t("topbar.lock")}</label>
              <label className="switch">
                <input
                  id="movableMast"
                  type="checkbox"
                  checked={!movableMast}
                  onChange={() => setMovableMast(!movableMast)}
                />
                <span className="slider"></span>
              </label>
            </div>
            <button className="btn btn-success" onClick={() => setShowConfig(true)}>
              <i className="fa-solid fa-gear"></i>
            </button>

            <div>
              <button
                className="btn btn-success"
                onClick={() => {
                  setHangers(buildHangers(scaleValues));
                  setMastValue(getCenterValue(scaleValues));
                  setHoverHanger(null);
                  setDrag(null);
                }}
              >
                <i className="fa-solid fa-arrow-rotate-left"></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="layout">
        <main className="stage">
          <div className="balanceContainer">
            <div className="balanceScaler" style={{ transform: `scale(${scale})` }} ref={wrapRef}>
              <div
                className="balanceWrap"
                style={{
                  "--cols": scaleValues.length
                }}
              >
                <div
                  className="beamGroup"
                  style={{
                    transform: `rotate(${rotationDeg}deg)`,
                    transformOrigin: `${mastLeftPct}% 50%`
                  }}
                >
                  <div className="hangersRow">
                    {hangers.map((h, index) => {
                      const isMiddle = index === middleIndex;

                      return (
                        <div
                          key={h.id}
                          ref={el => {
                            hangerRefs.current[h.id] = el;
                          }}
                          className={`hanger ${isMiddle ? "center" : ""} ${hoverHanger === h.id ? "hover" : ""}`}
                          data-hanger-id={h.id}
                        >
                          <div className="hanger-pin" />

                          <div className="weights">
                            {h.weightCount > 0 && (
                              <div
                                className="weight weightSingle"
                                onPointerDown={e => onWeightPointerDown(e, h.id)}
                              >
                                <div className="weightCount">{h.weightCount}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="scale">
                    {scaleValues.map((v, idx) => (
                      <div key={idx} className="tick">
                        <div className="tickLabel">{v}</div>
                      </div>
                    ))}
                  </div>

                  <div className="beam" ref={barRef} />
                </div>

                {showMastNow && (
                  <>
                    <div
                      className={`mast ${cfg.mastMode === "movable" ? "movable" : ""}`}
                      style={{ left: `${mastLeftPct}%` }}
                      onPointerDown={onMastPointerDown}
                    >
                      <div className="mastStem">
                        <input
                          type="text"
                          inputMode="decimal"
                          className="mastInput"
                          value={mastRawValue}
                          onPointerDown={e => e.stopPropagation()}
                          onFocus={() => { mastInputFocused.current = true; }}
                          onBlur={() => {
                            mastInputFocused.current = false;
                            // Al salir del campo, normalizar el valor mostrado
                            setMastRawValue(String(mastValue));
                          }}
                          onChange={e => {
                            const raw = e.target.value;
                            // Permitir: vacío, "-", dígitos, un separador decimal (punto o coma)
                            if (!/^-?(\d*[.,]?\d*)$/.test(raw)) return;
                            setMastRawValue(raw);
                            // Convertir coma a punto para parsear
                            const normalized = raw.replace(',', '.');
                            const num = parseFloat(normalized);
                            if (!isNaN(num)) {
                              setMastValue(num);
                            }
                          }}
                          disabled={!movableMast}
                        />
                        <img src="/images/Mastil.png" width={300} alt="" />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="weight-tray">
            <div
              className="weight tray-weight"
              onPointerDown={onTrayWeightPointerDown}
            >
              <div className="weightCount"><img src="/images/Peso.png" width={50} alt="" /></div>
            </div>
          </div>
          {drag && (
            <div
              className="dragGhost"
              style={{
                left: drag.x,
                top: drag.y
              }}
            />
          )}
        </main>

        <aside className="stats-sidebar">
          <div className="sidebar-inner">
            <div className="sidebar-title">{t("stats.title")}</div>

            {!statsEnabled ? (
              <div className="sidebar-disabled-msg">
                {t("stats.mastRequired")}
              </div>
            ) : (
              <>
                <div className="sidebar-check-group">
                  <label className="sidebar-check">
                    <input type="checkbox" checked={cfg.showStats}
                      onChange={e => updateCfg({showStats: e.target.checked})} />
                    {t("stats.enable")}
                  </label>
                  <label className="sidebar-check">
                    <input type="checkbox" checked={cfg.showMean}
                      disabled={!cfg.showStats}
                      onChange={e => updateCfg({showMean: e.target.checked})} />
                    {t("stats.mean")}
                  </label>
                  <label className="sidebar-check">
                    <input type="checkbox" checked={cfg.showMedian}
                      disabled={!cfg.showStats}
                      onChange={e => updateCfg({showMedian: e.target.checked})} />
                    {t("stats.median")}
                  </label>
                  <label className="sidebar-check">
                    <input type="checkbox" checked={cfg.showMode}
                      disabled={!cfg.showStats}
                      onChange={e => updateCfg({showMode: e.target.checked})} />
                    {t("stats.mode")}
                  </label>
                  <label className="sidebar-check">
                    <input type="checkbox" checked={cfg.showRange}
                      disabled={!cfg.showStats}
                      onChange={e => updateCfg({showRange: e.target.checked})} />
                    {t("stats.range")}
                  </label>
                  <label className="sidebar-check">
                    <input type="checkbox" checked={cfg.showVariance}
                      disabled={!cfg.showStats}
                      onChange={e => updateCfg({showVariance: e.target.checked})} />
                    {t("stats.variance")}
                  </label>
                  <label className="sidebar-check">
                    <input type="checkbox" checked={cfg.showStdDev}
                      disabled={!cfg.showStats}
                      onChange={e => updateCfg({showStdDev: e.target.checked})} />
                    {t("stats.stdDev")}
                  </label>
                  <label className="sidebar-check">
                    <input type="checkbox" checked={cfg.showCoefVar}
                      disabled={!cfg.showStats}
                      onChange={e => updateCfg({showCoefVar: e.target.checked})} />
                    {t("stats.coefVar")}
                  </label>
                </div>

                <StatsPanel hangers={hangers} cfg={cfg} />
              </>
            )}

            <div className="sidebar-lang">
              <button className="lang-toggle" onClick={toggleLang}>
                <span className="lang-active">{i18n.language === "es" ? "ES" : "EN"}</span>
                <span className="lang-sep">/</span>
                <span className="lang-inactive">{i18n.language === "es" ? "EN" : "ES"}</span>
              </button>
            </div>
          </div>
        </aside>

      </div>

      {/* Overlay para orientación portrait */}
      <div className="rotate-overlay">
        <div className="rotate-content">
          <div className="rotate-icon">⟳</div>
          <p>{t("rotate")}</p>
        </div>
      </div>

      {showConfig && (
        <div className="modal fade show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t("config.title")}</h5>
                <button className="btn-close" onClick={() => setShowConfig(false)}></button>
              </div>

              <div className="modal-body">
                <div className="row my-2">
                  <div className="col-4">
                    <label className="col-form-label">{t("config.hangers")}</label>
                  </div>
                  <div className="col-8">
                    <input
                      className="form-control"
                      type="number"
                      min="1"
                      step="2"
                      max="37"
                      value={cfg.hangersCount}
                      onChange={e => onChangeHangersCount(e.target.value)}
                    />
                  </div>
                </div>

                <div className="row my-2">
                  <div className="col-4">
                    <label className="col-form-label">{t("config.firstHanger")}</label>
                  </div>
                  <div className="col-8">
                    <input
                      className="form-control"
                      type="number"
                      value={cfg.startValue}
                      onChange={e => onChangeNumber("startValue", e.target.value)}
                    />
                  </div>
                </div>

                <div className="row my-2">
                  <div className="col-4">
                    <label className="col-form-label">{t("config.step")}</label>
                  </div>
                  <div className="col-8">
                    <input
                      className="form-control"
                      type="number"
                      value={cfg.step}
                      onChange={e => onChangeNumber("step", e.target.value)}
                    />
                  </div>
                </div>
                <div className="row my-2">
                  <div className="col-4">
                    <label htmlFor="showSumaDistancias">{t("config.distanceSum")}</label>
                  </div>
                  <div className="col-8">
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={cfg.showSumaDistancias}
                        onChange={e => updateCfg({ showSumaDistancias: e.target.checked })}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowConfig(false)}>
                  {t("config.close")}
                </button>
              </div>
            </div>
          </div>

          <div
            className="modal-backdrop fade show"
            onClick={() => setShowConfig(false)}
          ></div>
        </div>
      )}
    </div>
  );
}